// Shared motion vocabulary — every animated component imports from
// here so durations and easings stay consistent across the app.

import { useReducedMotion } from 'framer-motion'
import type { Variants, Transition } from 'framer-motion'

export const DUR = { fast: 0.15, base: 0.25, slow: 0.5, celebration: 0.8 } as const

export const SPRING_MODAL: Transition = { type: 'spring', stiffness: 380, damping: 30 }
export const SPRING_REVEAL: Transition = { type: 'spring', stiffness: 200, damping: 24 }
export const SPRING_CELEBRATION: Transition = { type: 'spring', stiffness: 260, damping: 20 }

export const pageVariants: Variants = {
  initial: { opacity: 0, y: 8 },
  enter: { opacity: 1, y: 0, transition: { duration: DUR.base, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: DUR.fast } },
}

export const staggerContainer: Variants = {
  initial: {},
  enter: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
}

export const cardItem: Variants = {
  initial: { opacity: 0, y: 12 },
  enter: { opacity: 1, y: 0, transition: { duration: DUR.base, ease: 'easeOut' } },
}

export const modalVariants: Variants = {
  initial: { opacity: 0, scale: 0.92 },
  enter: { opacity: 1, scale: 1, transition: SPRING_MODAL },
  exit: { opacity: 0, scale: 0.95, transition: { duration: DUR.fast } },
}

export const backdropVariants: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: { duration: DUR.fast } },
  exit: { opacity: 0, transition: { duration: DUR.fast } },
}

export const toastVariants: Variants = {
  initial: { opacity: 0, x: 48 },
  enter: { opacity: 1, x: 0, transition: { duration: DUR.base, ease: 'easeOut' } },
  exit: { opacity: 0, x: 48, transition: { duration: DUR.fast } },
}

// Broadcast lower-third banner: slides in from the left with overshoot
export const bannerSlideVariants: Variants = {
  initial: { opacity: 0, x: -120 },
  enter: { opacity: 1, x: 0, transition: SPRING_CELEBRATION },
  exit: { opacity: 0, x: 80, transition: { duration: DUR.fast } },
}

// Radial light flash behind celebration content
export const radialFlashVariants: Variants = {
  initial: { opacity: 0.9, scale: 0 },
  enter: { opacity: 0, scale: 2.5, transition: { duration: DUR.celebration, ease: 'easeOut' } },
}

const reducedFade: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: { duration: DUR.fast } },
  exit: { opacity: 0, transition: { duration: DUR.fast } },
}

/** Collapse any variants to a simple fade when the user prefers reduced motion */
export function useMotionVariants(variants: Variants): Variants {
  const reduced = useReducedMotion()
  return reduced ? reducedFade : variants
}
